import { HotspotWithLabel, WorkspaceConfirmationModalName } from "@nova/constants"
import { InfraredImage } from "features/data-management/hotspots/editor/infrared-image/InfraredImage"
import { selectInfraredFlightById } from "features/entity-caching/flights/selectors"
import {
    selectNonRemovedHotspotImageIdMap,
    selectHotspotWithLabelById,
} from "features/entity-caching/hotspots/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { InformationRow } from "panels/content/side-panel/upload-flow/preview-files/GenericImagePreviewModal"
import { PolygonResult, processPolygon } from "./polygonToPerimeter"
import { ButtonDestructive, ButtonPrimary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { InfraredFlight } from "@nova/protobuf"
import { selectInfraredImageById } from "features/entity-caching/images/hotspots/selectors"
import { selectHotspotPanelItems } from "./selectors"
import { closeHotspotPanel, openHotspotPanel } from "./slice"
import { useEffect } from "react"
import { setWorkspaceConfirmationModalContext } from "features/content-overlay/slice"
import { showGlobalModal } from "runlevel/actions"
import { selectMergedFlightById } from "features/entity-caching/mergedFlights/selectors"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { selectColorFinderIsOpen } from "plugins/ColorFinder/V2/redux/selectors"

const HotspotPreviewModal = () => {
    const selected = useAppSelector(selectHotspotPanelItems)
    const hotspot = useAppSelector(selectHotspotWithLabelById(selected.hotspotId))
    const flightId = hotspot?.flightId || ""
    const flight = useAppSelector(selectInfraredFlightById(flightId))
    const colorfinderOpen = useAppSelector(selectColorFinderIsOpen)

    if (!hotspot || !flight || colorfinderOpen) return null

    return <HotspotPreviewModalBody flight={flight} hotspot={hotspot} />
}

const HotspotPreviewModalBody = ({
    hotspot,
    flight,
}: {
    hotspot: HotspotWithLabel
    flight: InfraredFlight
}) => {
    const polygon = processPolygon(hotspot.polygon)
    const mergedFlight = useAppSelector(selectMergedFlightById(flight.mergeId ?? ""))
    const dispatch = useAppDispatch()
    const image = useAppSelector(selectInfraredImageById(hotspot.imageId))
    const imageMap = useAppSelector(selectNonRemovedHotspotImageIdMap(flight.id))

    const { isEditor } = useWorkspacePermissions()

    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === "Escape") dispatch(closeHotspotPanel())
        }
        addEventListener("keydown", handleKeyPress)
        return () => removeEventListener("keydown", handleKeyPress)
    }, [dispatch])

    /* Evil hack to determine if the click was a map click. Given a click event, we can
    check if any of the first 3 elements in the vertical slice of the click position
    are the map (our #map shows up third). I'm sure there's a better way to do this */
    const isMapClick = (e: MouseEvent) => {
        const clickList = document.elementsFromPoint(e.clientX, e.clientY)
        return clickList.slice(0, 3).some((item) => item.id === "map")
    }

    return (
        <ClickAwayListener
            onClickAway={(e) => {
                if (!("clickedHotspot" in e) && isMapClick(e)) dispatch(closeHotspotPanel())
            }}
            className="rounded-md bg-black-800 p-2 max-w-[288px]"
        >
            <div className="flex flex-col gap-3 text-white-1000">
                <div className="max-h-[250px]">
                    {hotspot && image && (
                        <InfraredImage
                            context={imageMap.get(image.id)}
                            infraredImage={image}
                            excludeY
                            zoomable={false}
                            isDemo={!!flight.demoId}
                        />
                    )}
                </div>
                <div className="flex flex-col gap-0.5">
                    <h5>
                        {mergedFlight?.name || flight.name} / #{hotspot.label}
                    </h5>
                    {hotspot.imageId !== "mock" && (
                        <small className="truncate text-gray-300">{hotspot.imageId}</small>
                    )}
                </div>
                <div>
                    <h5 className="text-gray-300 mb-1">INFORMATION</h5>
                    <InformationRow title="Sensitivity" value={hotspot.confidence.toFixed(3)} />
                    {polygon && (
                        <InformationRow
                            title="Hotspot Dimensions"
                            value={getHotspotDimensions(polygon)}
                        />
                    )}
                    {polygon && (
                        <InformationRow title="Hotspot Area" value={getHotspotArea(polygon)} />
                    )}
                    {!!hotspot.distanceToPerimeter && (
                        <InformationRow
                            title="Distance from Perimeter"
                            value={hotspot.distanceToPerimeter?.toString() ?? ""}
                        />
                    )}

                    <InformationRow
                        title="Coordinate"
                        value={`${hotspot.latitude.toFixed(4)}°, ${hotspot.longitude.toFixed(4)}°`}
                    />
                </div>
                <div className="flex flex-col gap-2">
                    <ButtonPrimary
                        title="See Full Details"
                        RightIcon={Icons.ExpandContent}
                        onClick={() => dispatch(openHotspotPanel())}
                    />
                    {isEditor && (
                        <ButtonDestructive
                            title="Remove Hotspot"
                            LeftIcon={Icons.Trash}
                            onClick={() => {
                                dispatch(
                                    setWorkspaceConfirmationModalContext({
                                        entityName: hotspot.label,
                                        entityId: hotspot.id,
                                        secondaryKey: flight.id,
                                        type: "delete-hotspot" as WorkspaceConfirmationModalName,
                                    })
                                )
                                dispatch(showGlobalModal())
                            }}
                        />
                    )}
                </div>
            </div>
        </ClickAwayListener>
    )
}

const getHotspotDimensions = (polygon: PolygonResult) =>
    `${polygon.boundingBox.heightFeet.toFixed(1)} ft x ${polygon.boundingBox.widthFeet.toFixed(1)} ft`

const getHotspotArea = (polygon: PolygonResult) =>
    `${(polygon.boundingBox.heightFeet * polygon.boundingBox.widthFeet).toFixed(1)} ft²`

export { HotspotPreviewModal }
